/**
 * Validation Result
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validation Rule Interface
 */
export interface ValidationRule<T> {
  validate(value: T): ValidationResult;
}

/**
 * Email Validation Rule
 */
export class EmailValidationRule implements ValidationRule<string> {
  validate(email: string): ValidationResult {
    const errors: string[] = [];
    
    if (!email) {
      errors.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push('Email must be a valid email address');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

/**
 * Password Validation Rule
 */
export class PasswordValidationRule implements ValidationRule<string> {
  validate(password: string): ValidationResult {
    const errors: string[] = [];
    
    if (!password) {
      errors.push('Password is required');
    } else {
      if (password.length < 6) {
        errors.push('Password must be at least 6 characters long');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

/**
 * Required Field Validation Rule
 */
export class RequiredValidationRule implements ValidationRule<string> {
  constructor(private readonly fieldName: string) {}
  
  validate(value: string): ValidationResult {
    const errors: string[] = [];
    
    if (!value || value.trim().length === 0) {
      errors.push(`${this.fieldName} is required`);
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

/**
 * Composite Validator
 */
export class CompositeValidator<T> {
  private rules: ValidationRule<T>[] = [];

  addRule(rule: ValidationRule<T>): CompositeValidator<T> {
    this.rules.push(rule);
    return this;
  }

  validate(value: T): ValidationResult {
    const allErrors: string[] = [];
    
    for (const rule of this.rules) {
      const result = rule.validate(value);
      if (!result.isValid) {
        allErrors.push(...result.errors);
      }
    }
    
    return {
      isValid: allErrors.length === 0,
      errors: allErrors
    };
  }
}

/**
 * Validator Factory
 */
export class ValidatorFactory {
  static createEmailValidator(): CompositeValidator<string> {
    return new CompositeValidator<string>()
      .addRule(new RequiredValidationRule('Email'))
      .addRule(new EmailValidationRule());
  }

  static createPasswordValidator(): CompositeValidator<string> {
    return new CompositeValidator<string>()
      .addRule(new RequiredValidationRule('Password'))
      .addRule(new PasswordValidationRule());
  }

  static createNameValidator(fieldName: string): CompositeValidator<string> {
    return new CompositeValidator<string>()
      .addRule(new RequiredValidationRule(fieldName));
  }
}
